import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import Gallery from "@/components/gallery";
import { useEmbeddyCrockerApiClient } from "@/hooks/useApiClient";
import LoadingGrid from "@/components/loaders/loadingGrid";
import FilterChip from "@/components/filterChip";
import { AISearchFAQ } from "@/components/AISearch/aiImageSearchFAQ";
import FocusedThumbnail from "@/components/gallery/focusedThumbnail";
import { useQuery } from "@tanstack/react-query";
import { IoClose } from "react-icons/io5";
import useDebouncedQueryParams from "@/hooks/useDebouncedQueryParams";
import { useAISearchErrorToast } from "@/hooks/useAISearchErrorToast";
import { zip } from "@/utils";
import {
  isValidUrl,
  mapDropdownFilterValuesToJSONPath,
} from "@/utils/aiSearchUtil";
import AIImageMetadataContent from "@/components/AISearch/aiImageMetadataContent";
import AISearchImageCard from "@/components/AISearch/aiSearchImageCard";
import AIImageCaption from "@/components/AISearch/aiImageCaption";
import { dropdownHandlerForQueryParamsFactory } from "@/components/dropdowns/utils";
import {
  CruiseDropdown,
  RovLoweringDropdown,
} from "@/components/realAsset/realAssetDeploymentDropdowns";
import { AIImageSearchSourcesDropdown } from "@/components/AISearch/aiSourcesDropdown";
import { getThumbnailUrlForMakoImages } from "@/apiUtils";
import { getEnv } from "@/utils/env";

const exampleUrls = {
  dev: [
    `data_asset/ca927d99-0836-4d8a-88b5-ed63672777ef?download=false`,
    `data_asset/8f30d3e2-c100-42a9-8464-afded4855168?download=false`,
    `data_asset/7f7c3c6a-569d-498e-8094-32e9a83d0593?download=false`,
    `data_asset/7caceb16-be01-4465-972e-498d1b25535d?download=false`,
    `data_asset/19aff121-d483-46c5-8cf1-7985d7e4bf13?download=false`,
    `data_asset/940a7708-d78c-4a41-8951-eea50220bbc4?download=false`,
    `data_asset/cdcc7e11-7f02-469d-91eb-7c2dcfbf0b79?download=false`,
    `data_asset/e915dff0-8cdd-491d-b3de-bd81c836600f?download=false`,
    `data_asset/b219c761-a81c-416f-bcb1-61059840e860?download=false`,
  ],
  stg: [
    `data_asset/ca927d99-0836-4d8a-88b5-ed63672777ef?download=false`,
    `data_asset/8f30d3e2-c100-42a9-8464-afded4855168?download=false`,
    `data_asset/7f7c3c6a-569d-498e-8094-32e9a83d0593?download=false`,
    `data_asset/7caceb16-be01-4465-972e-498d1b25535d?download=false`,
    `data_asset/19aff121-d483-46c5-8cf1-7985d7e4bf13?download=false`,
    `data_asset/940a7708-d78c-4a41-8951-eea50220bbc4?download=false`,
    `data_asset/cdcc7e11-7f02-469d-91eb-7c2dcfbf0b79?download=false`,
    `data_asset/e915dff0-8cdd-491d-b3de-bd81c836600f?download=false`,
    `data_asset/b219c761-a81c-416f-bcb1-61059840e860?download=false`,
  ],
  prod: [
    `data_asset/a7f9af98-20f3-448c-bae7-4d1e7902e710?download=false`,
    `data_asset/5370f7ae-0e5f-42cc-948d-557fe538e5ec?download=false`,
    `data_asset/d1302e52-c009-4ca1-9581-66732dbec037?download=false`,
    `data_asset/7eae25ae-6383-4748-a714-d6f588eb1bd9?download=false`,
    `data_asset/0a9721aa-ea31-427c-9618-584702174347?download=false`,
    `data_asset/12ba6aa0-e06d-4fe3-b0cc-d1fc9e327269?download=false`,
    `data_asset/c48d3813-b933-42d0-95d3-240b7f7a132f?download=false`,
    `data_asset/f77f7037-61c7-4b1e-a28d-e7f212603216?download=false`,
    `data_asset/0d5d86bc-69d4-492f-a282-2ba912da159f?download=false`,
  ],
};

const exampleMetadata = [
  {
    extra: {
      cruise_external_id: "OXR20201007",
      deployment_external_id: "CHR0015",
    },
  },
  {
    extra: {
      cruise_external_id: "OXR20220323",
      deployment_external_id: "CHR0232",
    },
  },
  {
    extra: {
      cruise_external_id: "OXR20210211",
      deployment_external_id: "CHR0063",
    },
  },
  {
    extra: {
      cruise_external_id: "OXR20210602",
      deployment_external_id: "CHR0102",
    },
  },
  {
    extra: {
      cruise_external_id: "OXR20220323",
      deployment_external_id: "CHR0243",
    },
  },
  {
    extra: {
      cruise_external_id: "OXR20220522",
      deployment_external_id: "CHR0283",
    },
  },
  {
    extra: {
      cruise_external_id: "OXR20210602",
      deployment_external_id: "CHR0115",
    },
  },
  {
    extra: {
      cruise_external_id: "OXR20220704",
      deployment_external_id: "CHR0323",
    },
  },
  {
    extra: {
      cruise_external_id: "OXR20210211",
      deployment_external_id: "CHR0066",
    },
  },
];

const getExampleImages = (env: keyof typeof exampleUrls, urlBase: string) => {
  const urls = exampleUrls[env].map((url: string) => `${urlBase}/${url}`);
  return Array.from(zip(urls, exampleMetadata)).map(([url, meta]) => ({
    url,
    ...meta,
  }));
};


const searchSchema = z.object({
  sources: z.array(z.string()).optional().default(["sealog-hd-stills", "fishbase", "sealifebase"]),
  url: z.string().optional(),
  size: z.number().optional().default(36),
  cruises: z.array(z.string()).optional().default([]),
  rovLowerings: z.array(z.string()).optional().default([]),
});

type AISearchQueryParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/AISearch/")({
  component: AISearch,
  validateSearch: searchSchema,
});

function AISearch() {
  const initialImages = getExampleImages(
    (getEnv().NEXT_PUBLIC_ENV ?? "prod") as keyof typeof exampleUrls,
    getEnv().NEXT_PUBLIC_CLIENT_SIDE_API_URL
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState("");
  const api = useMemo(useEmbeddyCrockerApiClient, []);
  const faqRef = useRef<HTMLDivElement>(null);

  const showErrorToast = useMemo(useAISearchErrorToast, []);

  const scrollToFAQ = () => {
    faqRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Search params are validated by the route's validateSearch schema.
  const { rawValues: filterValues, setRawValues: setFilterValues } =
    useDebouncedQueryParams<AISearchQueryParams>([], {
      sources: ["sealog-hd-stills", "fishbase", "sealifebase"],
      size: 36,
      cruises: [],
      rovLowerings: [],
    });

  const hasSearchParams = isValidUrl(filterValues.url ?? "");

  const {
    data: similarImages,
    isLoading: similarImagesAreLoadingOrDisabled,
    fetchStatus: similarImagesFetchStatus,
  } = useQuery({
    queryKey: ["ai-search-similarity-", ...Array.from(Object.entries(filterValues))],
    queryFn: async () => {
      try {
        const resp = await api.imageEmbedding.getSimilarImages({
          url: filterValues.url,
          size: filterValues.size,
          limitResultsToSources: filterValues.sources,
          extraJsonpathInclude: mapDropdownFilterValuesToJSONPath(
            filterValues.cruises,
            filterValues.rovLowerings
          ) ?? undefined,
        });
        return (resp as any).items ?? [];
      } catch (e) {
        showErrorToast("No Images Found!");
        return [];
      }
    },
    enabled: hasSearchParams,
    throwOnError: true,
    refetchOnWindowFocus: false,
  });
  const similarImagesAreLoading =
    similarImagesAreLoadingOrDisabled && similarImagesFetchStatus !== "idle";
  const hasFetchedResults = hasSearchParams && !similarImagesAreLoading;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearch = () => {
    setFilterValues({ url: inputValue });
  };

  useEffect(() => {
    setInputValue(filterValues.url ?? "");
  }, [filterValues.url]);

  const clearInput = () => {
    setFilterValues({ url: "", sources: ["sealog-hd-stills", "fishbase", "sealifebase"], size: 36, cruises: [], rovLowerings: [] });
    inputRef.current?.focus();
  };

  const makeDropdownChangeHandler = dropdownHandlerForQueryParamsFactory({
    rawValues: filterValues,
    setRawValues: setFilterValues,
  });
  const handleSourceDropdownFilterChange = makeDropdownChangeHandler("sources");
  const handleCruisesDropdownFilterChange =
    makeDropdownChangeHandler("cruises");
  const handleROVLoweringDropdownFilterChange =
    makeDropdownChangeHandler("rovLowerings");

  const getThumbnailForVideoOrRedirectForImage = (image: { url: string }) => {
    const isMakoUrl = image.url.startsWith(
      getEnv().NEXT_PUBLIC_CLIENT_SIDE_API_URL
    );
    const videoFrameTimestamp = image.url.match(/#t=(.+$)/)?.[1];
    if (isMakoUrl && videoFrameTimestamp) {
      return getThumbnailUrlForMakoImages(image);
    }
    return image.url;
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-white px-10 pb-16 pt-[2.36rem]">
      <h1 className="text-center text-4xl text-blue-200">AI Image Search</h1>
      <div className="flex h-full flex-col pt-[2rem]">
        {!filterValues.url && (
          <div className="my-4 text-center">
            <span className="mb-2 font-light text-blue-400">
              Enter a Mako URL or select an example image to get started!{" "}
            </span>
            <button
              onClick={scrollToFAQ}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textDecoration: "underline dotted",
              }}
              className="text-gray-400 hover:text-blue-400"
              onMouseOut={(e) =>
                (e.currentTarget.style.textDecorationColor = "inherit")
              }
            >
              What is this?
            </button>
          </div>
        )}
        {filterValues.url && (
          <FocusedThumbnail
            imageSrc={filterValues.url}
            thumbnailSrc={getThumbnailForVideoOrRedirectForImage({
              url: filterValues.url,
            }) ?? filterValues.url}
            ImageMetadataComponent={AIImageMetadataContent}
            ImageCaptionComponent={AIImageCaption}
          ></FocusedThumbnail>
        )}
        <div className="flex justify-center">
          <div className="grid w-4/5 max-w-4xl gap-4">
            {/* Row 1: Search input and button */}
            <div className="flex gap-4">
              <div className="relative flex-grow">
                <input
                  type="text"
                  className="form-input block w-full rounded pr-9 text-blue-400"
                  placeholder="Enter Image URL"
                  ref={inputRef}
                  value={inputValue || ""}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                {filterValues.url && (
                  <button
                    onClick={clearInput}
                    className="absolute inset-y-0 right-0 flex items-center px-4 text-grey-200 hover:text-blue-500"
                    aria-label="Clear input"
                  >
                    <IoClose size={32} />
                  </button>
                )}
              </div>
              <button
                onClick={handleSearch}
                className="rounded bg-blue-500 px-4 py-2 text-white"
              >
                Search
              </button>
            </div>

            {/* Row 2: Dropdowns */}
            {hasFetchedResults && (
              <div className="z-10 flex gap-2 align-middle">
                <span className="pt-[0.4rem] font-medium text-blue-300">
                  Filter Results By:
                </span>
                <AIImageSearchSourcesDropdown
                  selections={filterValues.sources}
                  onOptionToggle={handleSourceDropdownFilterChange}
                />
                <CruiseDropdown
                  selections={filterValues.cruises}
                  onOptionToggle={(d) =>
                    handleCruisesDropdownFilterChange(d.external_id)
                  }
                  mapOptionToSelection={(o) => o.external_id}
                />
                <RovLoweringDropdown
                  selections={filterValues.rovLowerings}
                  onOptionToggle={(d) =>
                    handleROVLoweringDropdownFilterChange(d.external_id)
                  }
                  cruises={filterValues.cruises}
                  mapOptionToSelection={(o) => o.external_id}
                />
              </div>
            )}

            <div className={"flex flex-wrap gap-2"}>
              {filterValues.sources.map((f) => (
                <FilterChip
                  key={f}
                  title={"Source"}
                  label={f}
                  onClose={() => handleSourceDropdownFilterChange(f)}
                />
              ))}
              {filterValues.cruises.map((f) => (
                <FilterChip
                  key={f}
                  title={"Cruise"}
                  label={f}
                  onClose={() => handleCruisesDropdownFilterChange(f)}
                />
              ))}
              {filterValues.rovLowerings.map((f) => (
                <FilterChip
                  key={f}
                  title={"ROV Lowering"}
                  label={f}
                  onClose={() => handleROVLoweringDropdownFilterChange(f)}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-lg pt-2">
          {hasSearchParams && similarImagesAreLoading ? (
            <LoadingGrid />
          ) : (
            <Gallery
              images={
                !hasSearchParams || similarImagesAreLoading
                  ? initialImages
                  : similarImages
              }
              onImageSearchClick={(url: string) => {
                setFilterValues({ url });
              }}
              getThumbnailUrl={getThumbnailForVideoOrRedirectForImage as (image: { url: string }) => string}
              ImageCardComponent={AISearchImageCard}
              hasFetchedResults={hasFetchedResults}
            />
          )}
        </div>
        <div ref={faqRef} className="flex justify-center">
          <AISearchFAQ />
        </div>{" "}
      </div>
    </div>
  );
}
